// Pending-balance model (FM-2098) — shared by Portfolio and Dashboard.
//
// One normalized shape, produced at the adapter boundary so no component ever
// sees a raw API row (Hard Rule #2):
//   { total, settled, pending, asOf, pendingItems: [{ type, label, amount, status, date }] }
//
// `settled` is the balance the fund positions add up to (`account.totalBalance`) —
// money that is still pending isn't invested yet, so `total = settled + pending`.
//
// Neither endpoint ships account-level pending figures. What they DO ship is the
// per-transaction `status` on activity rows and the in-flight rebalance on
// /manage-account's `rebalanceState`; both fold into `pendingItems` here.
//
// `amount: null` marks a net-zero in-flight transaction — a rebalance moves units
// between funds without changing the balance, so there is no dollar figure to show.
// Callers render those as a neutral "processing" note, never as a pill.
//
// This module was extracted from `portfolio.adapters.js` (unchanged) when the
// Dashboard hero needed the same model for its pending strip: the design carries one
// pending concept (`plan-pending.js`), and two implementations would drift the way
// the activity-icon map already did. Pure — no React, no i18n, like `utils/contributions.js`.
import { formatFullDate } from './format';

export const round2 = (n) => Math.round(n * 100) / 100;

/** Wire transaction category → the pending model's `type` union. */
const PENDING_TYPE = {
  contrib: 'contribution',
  contribution: 'contribution',
  paycredit: 'credit',
  credit: 'credit',
  trade: 'trade',
  rebal: 'rebalance',
  rebalance: 'rebalance',
  transfer: 'transfer',
  benef: 'transfer',
  dist: 'distribution',
  div: 'distribution',
  dividend: 'distribution',
};

// Statuses that mean "not settled yet". Explicit (rather than "anything that isn't
// posted") so a typo'd/unknown status is never silently counted as money.
const PENDING_STATUSES = new Set(['pending', 'processing', 'submitted', 'in_progress']);

export const isPending = (status) => PENDING_STATUSES.has(String(status || '').toLowerCase());

// Pending dates are display strings by the time a component sees them (one
// formatting boundary, per Hard Rule #2). Bare ISO dates go through the UTC-based
// formatter so "2026-08-15" never slips to the 14th in a negative-offset timezone;
// values a fixture already ships formatted pass straight through.
export const pendingDate = (d) => {
  if (!d) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(d) ? formatFullDate(d) : String(d);
};

/**
 * Activity rows the backend flags as not-yet-settled → pending items.
 * Works for both row shapes: the wire's `{category, heading, direction}` and a
 * mock's already-UI-ish `{type, desc, signed}`.
 * @param {Array} rows activity rows
 */
export const pendingFromActivity = (rows) =>
  (rows || [])
    .filter((a) => isPending(a.status))
    .map((a) => ({
      type: PENDING_TYPE[a.category ?? a.type] || 'contribution',
      label: a.heading ?? a.desc ?? null,
      // Net-zero transactions carry no amount → stays null (processing note only).
      amount: a.amount == null ? null : a.direction === 'neg' ? -a.amount : a.amount,
      status: String(a.status).toLowerCase(),
      date: pendingDate(a.date),
    }));

/**
 * An in-flight rebalance from /manage-account's `rebalanceState`. Always net-zero
 * (units move between funds), so `amount` is null by construction.
 * BACKEND TODO: rebalanceState carries no submitted date — `submittedDate` is read
 * opportunistically so the note picks the date up the moment the backend adds it.
 * @param {object|null} rb rebalanceState
 */
export const pendingFromRebalance = (rb) => {
  if (!rb) return [];
  const count = rb.pendingTransactions || 0;
  if (!count && String(rb.status || '').toLowerCase() !== 'rebalancing') return [];
  return [
    {
      type: 'rebalance',
      // No English label synthesized here — the component localizes the rebalance
      // from `count`. Keeps chrome strings out of the data layer so they translate
      // on es/fr/zh (CLAUDE.md §16).
      label: null,
      count,
      amount: null,
      status: 'processing',
      date: pendingDate(rb.submittedDate),
    },
  ];
};

/**
 * Pending items a fixture (or a future endpoint) states EXPLICITLY, rather than
 * leaving to be inferred from activity statuses. The dashboard mock takes this
 * route because its activity amounts are preformatted display strings
 * ("+$850.00") — parsing those back into numbers to re-derive a figure the
 * fixture already knows would be a worse contract. Mirrors the design, where
 * `plan-pending.js` reads `plan.pendingItems` before falling back to inference.
 * @param {Array} items snake_case or camelCase pending rows
 */
export const normalizePendingItems = (items) =>
  (items || []).map((i) => ({
    type: PENDING_TYPE[i.type] || i.type || 'contribution',
    label: i.label ?? null,
    amount: i.amount == null ? null : i.amount,
    status: String(i.status || 'pending').toLowerCase(),
    date: pendingDate(i.date),
    ...(i.count != null || i.funds != null ? { count: i.count ?? i.funds } : {}),
  }));

/**
 * Assemble the balance model. `settled` is the invested balance; `pending` is the
 * net of every pending item that has an amount; `total` is what the two add to.
 * @param {number|null} settled invested balance
 * @param {string|Date|null} asOf as-of date for the settled figure
 * @param {Array} items normalized pending items (possibly empty)
 */
export const buildBalance = (settled, asOf, items = []) => {
  const pendingItems = items.filter(Boolean);
  const pending = round2(pendingItems.reduce((sum, i) => sum + (i.amount || 0), 0));
  return {
    total: settled == null ? null : round2(settled + pending),
    settled: settled ?? null,
    pending,
    asOf: asOf ?? null,
    pendingItems,
  };
};

/**
 * The presentation view of a pending model — what a hero strip needs to decide
 * WHICH treatment to render, without re-walking the items itself.
 *
 * Three outcomes, and the distinction matters:
 *   • nothing pending          → `hasAny: false`, render no strip at all
 *   • money in flight          → `hasAmount: true`, state the dollar figure
 *   • net-zero in flight only  → `processingOnly: true`, a neutral processing note
 *     with NO dollar figure (a rebalance changes nothing about the balance, so
 *     "$0.00 pending" would be a false claim rather than a small one)
 *
 * `noteItems` is the authoritative list of items needing a processing note, and
 * callers must use it rather than re-deriving one. Both heroes previously filtered
 * `amount == null` themselves, which silently dropped items whose amounts CANCEL:
 * a +$500 credit beside a -$500 debit nets to zero, so `hasAmount` was false and no
 * figure rendered — yet both items carried real amounts, so neither passed the
 * `amount == null` filter either. The result was a mounted strip with a label, a link
 * and nothing in between, and two transactions the participant could not see anywhere.
 * Net-zero is a property of the SUM; "has no dollar split" is a property of an ITEM,
 * and conflating them is what lost them.
 *
 * Returns figures and counts only — never a sentence. The caller localizes
 * (CLAUDE.md §16).
 * @param {object|null} balance a model from `buildBalance`
 */
export const pendingView = (balance) => {
  const items = balance?.pendingItems || [];
  if (!items.length) {
    return {
      hasAny: false, hasAmount: false, processingOnly: false, amount: 0, count: 0, items: [], noteItems: [],
    };
  }
  const amount = round2(items.reduce((sum, i) => sum + (i.amount || 0), 0));
  const withAmount = items.filter((i) => i.amount != null);
  // A single net-zero item, or several that cancel out, is still not money to state.
  const hasAmount = withAmount.length > 0 && amount !== 0;
  return {
    hasAny: true,
    hasAmount,
    processingOnly: !hasAmount,
    amount,
    count: items.length,
    // Every item whose money the dollar figure does NOT account for. When a figure is
    // stated it covers the amount-bearing items, so only the null-amount ones need a
    // note; when no figure is stated (nothing has an amount, OR they cancel out)
    // EVERY item needs one, or it disappears from the screen.
    noteItems: hasAmount ? items.filter((i) => i.amount == null) : items,
    // The first item drives the strip's label/date; the rest are reachable for a
    // future multi-item treatment (the design shows one line today).
    primary: items[0],
    items,
  };
};

export default buildBalance;
